/**
 * Parcours client — l'adresse publique et rien d'autre.
 *
 * Le parcours n'est plus servi par le CRM : il a été porté en Twig sur le
 * site public (voir `export-twig/`), qui est désormais la seule version
 * maintenue. Les deux feuilles ont divergé le 27/08/2026 et le CRM ne doit
 * plus afficher la sienne.
 *
 * Ce module est volontairement SANS dépendance (ni Api, ni router) : il est
 * importé par `router.js`, et passer par `Api` y créerait un cycle
 * d'imports supplémentaire.
 *
 * Un seul point d'entrée l'utilise : `router.js`, où les anciennes routes
 * `/parcour/client/:api` et `/parcour/clientv2/:api` redirigent vers
 * l'adresse publique. Elles sont conservées : des liens sont déjà partis
 * chez des prospects, et l'API en fabrique encore (le bouton « Lien
 * Parcours » de la fiche prospect). Tous aboutissent sur la redirection,
 * donc au bon endroit — la bascule reste décidée à un seul endroit.
 */

/** Racine du parcours public. Une seule déclaration dans tout le projet. */
export const PARCOURS_BASE_URL = 'https://assureur-francais.fr/parcours';

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Extrait le jeton du parcours, quelle que soit la forme reçue :
 *
 *   '3f2b…-uuid'                                          → le jeton lui-même
 *   'https://assureur-francais.jg.app/#/parcour/clientv2/3f2b…'  → ancien lien CRM
 *   'https://assureur-francais.fr/parcours/3f2b…'          → déjà la bonne adresse
 *
 * L'UUID est cherché en priorité — c'est le format en vigueur et il reste
 * reconnaissable où qu'il se trouve dans l'URL, y compris après un `#` de
 * hash-router. À défaut, on retombe sur le dernier segment de chemin :
 * l'API a utilisé d'autres formats de jeton par le passé, et un lien qu'on
 * ne sait pas décoder ne doit pas devenir un lien mort.
 *
 * @param {string|false|null} value jeton brut ou URL complète
 * @returns {string|null} le jeton, ou null si rien d'exploitable
 */
export function parcoursToken(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const uuid = raw.match(UUID_RE);
  if (uuid) return uuid[0];

  const segments = raw.split('/').filter(Boolean);
  const last = segments.length ? segments[segments.length - 1] : '';
  const token = last.split('?')[0].split('#')[0];
  return token || null;
}

/**
 * Adresse publique du parcours pour un jeton (ou pour une ancienne URL).
 *
 * Idempotent : appliquée à une URL déjà correcte, la fonction la reconstruit
 * à l'identique. Renvoie `false` — et non une URL tronquée — quand il n'y a
 * rien à exploiter, pour rester compatible avec les tests `!== false` déjà
 * écrits côté vues.
 *
 * @param {string|false|null} value jeton brut ou URL complète
 * @returns {string|false}
 */
export function parcoursUrl(value) {
  const token = parcoursToken(value);
  return token ? `${PARCOURS_BASE_URL}/${token}` : false;
}
